/*
 *   File : customer.js
 *   Author : https://evoqins.com
 *   Description : Customer data tab file
 *   Integrations : 'react-toastify , date-fns'
 *   Version : 1.0.0
*/

// custom packages
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

// custom components
import { CustomTab } from '../../../Components/Others';
import { CustomDatePicker, CustomSearchInput, CustomSelectBox } from '../../../Components/FormElements';
import { PrimaryButton, SecondaryButton, TextButton } from '../../../Components/Buttons';
import { CustomDataTable } from '../../../Components/DataTable';
import { Icon } from '../../../Components/Icon';
import { APIService } from '../../../Service';
import EmptyScreen from '../../../Components/Others/empty-screen';

//  custom style
import color from '../../../Style/color.scss';

const TAB_DATA = [
    { id: 1, label: 'Invested', tooltip: false },
    { id: 2, label: 'Non-Invested', tooltip: true, tooltipContent: 'User who created investment account and didn’t invested yet' },
    { id: 3, label: 'Signed-up', tooltip: true, tooltipContent: 'User who signed up and left before creating investment account' }
];

const Customers = () => {

    const { state } = useLocation();
    const { id } = useParams();
    const navigate = useNavigate();
    const isClearRef = useRef(false);
    const isInitializedRef = useRef(false);

    const [selectedTab, setSelectedTab] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDate, setSelectedDate] = useState(null);

    const [selectedKycStatus, setSelectedKycStatus] = useState("");
    const [selectedRiskAppetite, setSelectedRiskAppetite] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [dateError, setDateError] = useState("");
    const [selectedType, setSelectedType] = useState("invested");
    const [checked, setChecked] = useState([]);


    const [listLoader, setListLoader] = useState(true);
    const [calendarToggle, setCalendarToggle] = useState(false);
    const [toggleFilter, setToggleFilter] = useState(false);
    const [filterLoader, setFilterLoader] = useState(true);
    const [searched, setSearched] = useState(false);
    const [onPaginationChange, setOnPaginationChange] = useState(false);

    const [addCustomerList, setAddCustomerList] = useState({});
    const [filterData, setFilterData] = useState({});
    const [filterTab, setFilterTab] = useState({});

    const [downloadLoader, setDownloadLoader] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
        if (isClearRef.current === true) {
            _addCustomers();
            setListLoader(true);
        }
    }, []);


    useEffect(() => {
        setSearchValue("")
        if (selectedTab != -1) {
            _addCustomers();
            setToggleFilter(false);
            setListLoader(true);
        }
    }, [selectedTab]);

    useEffect(() => {
        if (isInitializedRef.current === false) {
            _filterData();
        }
        isInitializedRef.current = true
    }, []);

    useEffect(() => {
        if (selectedType) {
            if (Object.keys(filterData).length !== 0) {
                let updated_filter_data = filterData[selectedType]

                setFilterTab(updated_filter_data)

                // if (selectedType === 'fresh_kyc_submitted') {
                //     const updated_fp_status = [{ value: null, label: 'All' }, ...updated_filter_data.fp_status];
                //     console.log("updated fp status", updated_fp_status);
                //     setFpStatusFilter(updated_fp_status)
                //     setSelectedFpStatus(updated_fp_status[0])

                // }
                //  else if (selectedType === 'bank_verification_list') {
                //     const updated_status = [{ value: null, label: 'All' }, ...updated_filter_data.status];
                //     setBankVerificationFilter(updated_status);
                //     setSelectedStatus(updated_status[0]);
                // }
                // else {
                setSelectedKycStatus(updated_filter_data?.kyc_status[0]);
                setSelectedRiskAppetite(updated_filter_data?.risk_appetite[0]);
                // }
            }
        }

    }, [filterData, selectedType]);

    useEffect(() => {
        if (state !== null) {
            if (state.customer_tab) {
                setSelectedTab(state.customer_tab);
                setSelectedType(state.type)

            } else {
                setSelectedTab(1);
            }
        } else {
            setSelectedTab(1);
        }
    }, [state]);


    const _handleState = (selected) => {
        setSelectedKycStatus(selected);
        isClearRef.current = false;
    };

    const _toggleAllRows = () => {
        if (checked.length === addCustomerList.customer_data.length) {
            setChecked([]);
        } else {
            setChecked(addCustomerList.customer_data.map(row => row.customer_id));
        }
    };

    const _handleChecked = (customer_id) => {
        setChecked(prev => {
            if (prev.includes(customer_id)) {
                return prev.filter(id => id !== customer_id);
            } else {
                return [...prev, customer_id]
            }
        })
    };

    // handle clear filter
    const _handleClear = () => {
        _handleClearDate();
        // if (selectedType === 'fresh_kyc_submitted') {
        //     setSelectedFpStatus(fpStatusFilter[0]);
        // } else if (selectedType === 'bank_verification_list') {
        //     setSelectedStatus(bankVerificationFilter[0]);
        // } else {
        setSelectedKycStatus(filterTab.kyc_status[0]);
        setSelectedRiskAppetite(filterTab.risk_appetite[0]);
        // }
        isClearRef.current = true;
    };

    // handle select date 
    const _handleSelectDate = (date) => {
        setSelectedDate(date);
        setCalendarToggle(false);
        isClearRef.current = false;
    };


    // handle toggle date picker 
    const _handleToggleCalendar = () => {
        setCalendarToggle(!calendarToggle);
    };
    // handle clear date
    const _handleClearDate = () => {
        setSelectedDate(null);
    };

    const _handleRiskAppetite = (selected) => {
        setSelectedRiskAppetite(selected);
        isClearRef.current = false;
    };

    // handle toggle filter 
    const _handleFilter = () => {
        setToggleFilter(!toggleFilter);
    }

    // handle table page change 
    const _handleTablePageChange = (page_num) => {
        setCurrentPage(page_num);
        _addCustomers(page_num);
    };

    // handle tab change 
    const _handleTab = (tab_id, type) => {
        setSelectedType(type)
        isClearRef.current = true;
        navigate(`/sub-admins/${id}`, {
            state: {
                tab_index: 1,
                customer_tab: tab_id,
                type: type
            }
        });
        setSelectedKycStatus("");
        setSelectedRiskAppetite("");
        // setSelectedFpStatus("");
        // setSelectedStatus("");
    };

    // handle navigate to customer detail
    const _handleCustomerDetail = (customer_id) => {
        navigate(`/customers/${customer_id} `, {
            state: {
                source: "normal_listing",
                tab_index: selectedTab,
                type: selectedType,
            }
        });
    };

    // handle search clear
    const _handleSearchClear = () => {
        setSearched(false);
        setSearchValue("");
        if (searched === true) {
            _addCustomers(currentPage, "");

        }
    };

    // on search click
    const _onSearch = () => {
        setSearched(true);
        setListLoader(true);
        _addCustomers();
    };

    // handle selected filter
    const _handleFilterSelected = () => {
        setListLoader(true);
        _addCustomers();
    };

    // Pending request data table column 
    const ORDER_TABLE_COLUMN = [
        {
            name: 'Customer ID',
            selector: row => row.rayi_customer_id,
            width: "160px",
            sortable: true
        },
        {
            name: 'Name',
            selector: row => row.name,
            width: "260px",
            sortable: true
        },
        {
            name: 'Joined on',
            selector: row => row.joined_on,
            width: "170px",
            sortable: true,
            selector: row => new Date(row.joined_on),  // 👈 converts string to Date object
            format: row => row.joined_on,
        },
        {
            name: 'Risk appetite',
            selector: row => row.risk_appetite || '-',
            width: "150px"
        },
        {
            name: 'KYC Status',
            selector: row => row.kyc_status,
            width: "160px"
        },
        {
            name: 'AUM',
            selector: row => row.aum_text,
            width: "160px",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.aum < b.aum) return -1;
                if (a.aum > b.aum) return 1;
                return 0;
            }
        },
        {
            name: 'Email',
            selector: row => row.email,
            width: "280px"
        },
        {
            name: 'Phone number',
            selector: row => row.phone,
            width: "170px"
        },
        {
            name: 'RM name',
            selector: row => row.rm_name,
            width: "120px"
        }
    ];



    // API - Add customer list
    const _addCustomers = (page_num = currentPage, search = searchValue) => {
        let is_post = true;
        const formatted_date = selectedDate === null ? null : format(new Date(selectedDate), 'yyyy-MM-dd');
        let url = 'sub-admin/customers';
        let request = {

            // "status": null,
            // "fp_status": 3,
            "joined_date": formatted_date,
            "kyc_status": selectedKycStatus.value,
            "risk_appetite": selectedRiskAppetite.value,
            "investment_status": selectedTab,
            "query": search,
            "id": id,
            "page_num": page_num,
            "page_size": "10"

        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setAddCustomerList(response.data)
            }
            else {
                setAddCustomerList([]);
                // toast.dismiss();
                // toast.error(response.message, { type: "error" });
            }
            setListLoader(false);
            setOnPaginationChange(false);
        })
    };


    // API - Get login otp
    const _filterData = () => {
        let is_post = false;
        let url = 'user/filter-data';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                setFilterData(response.data);
            } else {
                setFilterData([]);
                // toast.dismiss();
                // toast.success(response.message, { type: "error" });
            }
            setFilterLoader(false);
        });
    };

    return (
        <div className='pb-5'>
            <div className='padding-32px-lr padding-32px-top'>
                {/* Custom tab  */}
                <div className='d-flex align-items-center justify-content-start pb-4 gap-16px'>
                    <CustomTab data={TAB_DATA}
                        selectedTab={selectedTab}
                        handleClick={_handleTab} />
                </div>

                <div className='d-flex align-items-center justify-content-end gap-32px'>

                    {/* Search input */}
                    <CustomSearchInput placeholder="Search with customer ID or name"
                        type="text"
                        value={searchValue}
                        className="w-340px"
                        onSearchClear={_handleSearchClear}
                        onSearch={() => _onSearch()}
                        onClear={_handleSearchClear}
                        onKeyPress={() => _onSearch()}
                        handleChange={(e) => setSearchValue(e)} />

                    <div className={` padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer   ${toggleFilter && ' e-bg-blue-violet '}`}
                        onClick={filterLoader === false && _handleFilter}>
                        <Icon icon='filter'
                            size='1.25rem'
                            className='d-flex'
                            color={toggleFilter && color.white} />
                    </div>
                </div>
            </div>
            {/* Filter */}
            {toggleFilter &&
                <div className='e-bg-main-white pt-3 pb-4 padding-32px-lr mt-2'>
                    <div className='d-flex justify-content-end'>
                        <TextButton name="Clear all"
                            onClick={_handleClear} />
                    </div>
                    <div className='d-flex gap-4 padding-32px-bottom '>
                        <CustomSelectBox title="KYC status"
                            placeholder='Select'
                            width='15rem'
                            options={filterTab.kyc_status}
                            isSearchable={false}
                            value={selectedKycStatus}
                            onChange={_handleState} />

                        <CustomSelectBox title='Risk Appetite '
                            placeholder='Select'
                            width='15rem'
                            options={filterTab.risk_appetite}
                            isSearchable={false}
                            value={selectedRiskAppetite}
                            onChange={_handleRiskAppetite} />

                        <CustomDatePicker
                            label="Joined date"
                            placeholder="Select"
                            selected={selectedDate}
                            type={2}
                            toggle={calendarToggle}
                            error={dateError}
                            handleToggle={_handleToggleCalendar}
                            handleClose={_handleToggleCalendar}
                            handleClear={_handleClearDate}
                            handleSelect={_handleSelectDate}
                        />
                    </div>

                    <PrimaryButton name='Filter now'
                        paddingBlock='padding-12px-tb padding-44px-lr'
                        onClick={_handleFilterSelected} />
                </div>
            }

            <div className='pt-2 padding-32px-lr d-flex gap-1 align-items-center justify-content-end'>
                <div>
                    <img src={require("../../../Assets/Image/scroll-animation.gif")}
                        draggable={false}
                        className='e-rotate-270'
                        width={24}
                        height={24}
                        alt='scroll' />
                </div>
                <p className='mb-0 e-text-bleu-de-france e-alt-font e-font-12 e-font-weight-500 line-height-14px'>
                    Scroll horizontally for view more  information
                </p>
            </div>

            {/* Order list table */}
            {listLoader ?
                <div className='d-flex align-items-center justify-content-center min-vh-100'>
                    <img src={require("../../../Assets/Image/loader.gif")}
                        draggable={false}
                        width={80}
                        height={80}
                        alt='loader'
                        className='text-center' />
                </div>
                :
                addCustomerList?.customer_data?.length > 0 ?
                    <div className='e-data-table-wrapper pt-2 padding-32px-bottom'>
                        <CustomDataTable columns={ORDER_TABLE_COLUMN}
                            data={addCustomerList.customer_data}
                            pagination={addCustomerList.total_count > 10 ? true : false}
                            paginationPerPage={10}
                            lastPage={addCustomerList.total_page}
                            paginationTotalRows={addCustomerList.total_count}
                            paginationDefaultPage={addCustomerList.current_page}
                            onChangePage={_handleTablePageChange}
                            rowClick={(row) => _handleCustomerDetail(row.customer_id)} />
                    </div>
                    :
                    <EmptyScreen
                        image={require('../../../Assets/Image/NotFound/no-data.png')}
                        title='No results found'
                        description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                    />
            }
        </div>
    )
}

export default Customers;
